import {
	BufferGeometry,
	ConeGeometry,
	LineBasicMaterial,
	LineSegments,
	Mesh,
	MeshStandardMaterial,
	Quaternion,
	Scene,
	Vector3
} from 'three';

import {
	CAMERA_HELPER_DEFAULTS,
	CameraHelperObjects,
	getQualitySegments,
	RenderQuality,
	VisualizableCamera
} from './camera-helper.types';

/**
 * Creates visualization objects for a camera (cone, target line)
 *
 * @param camera The camera to create objects for
 * @param scene Scene to add objects to
 * @param coneRadius Radius of the cone base
 * @param coneHeight Height of the cone
 * @param coneColor Color of the cone
 * @param lineColor Color of the target line
 * @param visible Initial visibility state
 * @param targetPosition Optional target position for orientation
 * @param quality Render quality 0-10 (default: 5)
 * @returns Object containing cone and targetLine meshes
 */
export const createCameraObjects = (
	camera: VisualizableCamera,
	scene: Scene,
	coneRadius: number = CAMERA_HELPER_DEFAULTS.CONE_RADIUS,
	coneHeight: number = CAMERA_HELPER_DEFAULTS.CONE_HEIGHT,
	coneColor: number = CAMERA_HELPER_DEFAULTS.CONE_COLOR,
	lineColor: number = CAMERA_HELPER_DEFAULTS.LINE_COLOR,
	visible: boolean = false,
	targetPosition?: Vector3,
	quality: RenderQuality = CAMERA_HELPER_DEFAULTS.QUALITY
): CameraHelperObjects => {
	const segments = getQualitySegments(quality);

	// Create cone
	const coneGeometry = new ConeGeometry(coneRadius, coneHeight, segments.radial, segments.height);
	const coneMaterial = new MeshStandardMaterial({ color: coneColor, wireframe: false }); // Interface objects never wireframe
	const cone = new Mesh(coneGeometry, coneMaterial);
	cone.name = `cameraCone_${camera.uuid}`;
	cone.position.copy(camera.position);
	// Mark as interface object to exclude from wireframe mode
	cone.userData.isInterface = true;
	cone.userData.type = 'helper';

	// Orient cone toward target if provided
	if (targetPosition) {
		const direction = new Vector3()
			.subVectors(targetPosition, camera.position)
			.normalize();
		const up = new Vector3(0, -1, 0);
		const quaternion = new Quaternion();
		quaternion.setFromUnitVectors(up, direction);
		cone.setRotationFromQuaternion(quaternion);
	}

	cone.visible = visible;
	scene.add(cone);

	// Create target line
	const lineGeometry = new BufferGeometry();
	const lineMaterial = new LineBasicMaterial({ color: lineColor });
	const targetLine = new LineSegments(lineGeometry, lineMaterial);
	targetLine.name = `cameraTargetLine_${camera.uuid}`;
	// Mark as interface object to exclude from wireframe mode
	targetLine.userData.isInterface = true;
	targetLine.userData.type = 'helper';
	targetLine.visible = visible;
	scene.add(targetLine);

	return { cone, targetLine };
};

/**
 * Disposes and removes camera helper objects from scene
 *
 * @param objects Camera helper objects to dispose
 * @param scene Scene to remove objects from
 */
export const disposeCameraObjects = (
	objects: CameraHelperObjects,
	scene: Scene
): void => {
	// Remove from scene
	scene.remove(objects.cone);
	scene.remove(objects.targetLine);

	// Dispose geometries
	objects.cone.geometry.dispose();
	objects.targetLine.geometry.dispose();

	// Dispose materials
	(objects.cone.material as MeshStandardMaterial).dispose();
	(objects.targetLine.material as LineBasicMaterial).dispose();
};

